import type { ServicePageInsert } from '@/schemas'

/**
 * The four specialist advisory pages from the enhancement spec's `page_changes`.
 *
 * This module is the SINGLE canonical slug list. It is imported by the router,
 * `server/lib/seoTemplate.ts`, `server/lib/syndication.ts` and
 * `server/lib/seed.ts` — before this existed, adding a route meant editing five
 * hardcoded lists, and each one failed differently when missed (404 page, absent
 * sitemap entry, missing title, soft-404).
 *
 * Every page seeds `published: false` and `claimStatus: 'proposed_positioning'`.
 * The spec's `publishing_guardrails` require owner approval before publishing,
 * and `/api/service-pages` enforces it with `visibilityColumn: 'published'` — so
 * an unapproved page is genuinely absent from the public API, the sitemap and
 * the navigation, not merely hidden in the browser.
 *
 * Copy provenance, because it matters for review:
 *   - `/fractional-cto` is the ONLY page the spec fully specifies
 *     (`fractional_cto_page`): H1, fit triggers, process and engagement options
 *     are all its words.
 *   - The other three take `intro`, `scope` and `exclusions` from
 *     `services.proposed_offers`, but the spec supplies no H1 and no decision
 *     triggers for them. Those are authored here and need owner sign-off before
 *     the page goes live — which is exactly what `published: false` buys.
 *
 * `evidenceProjectSlugs` / `evidenceTestimonialIds` seed empty on purpose. The
 * spec's evidence rule is "use selected existing portfolio items and
 * testimonials with their original attribution only", so which 60-project rows
 * belong on which page is an owner decision, made in /admin.
 */
export const SERVICE_PAGE_SLUGS = [
  'fractional-cto',
  'technical-due-diligence',
  'payments-market-entry',
  'government-digital-collections',
] as const

export type ServicePageSlug = (typeof SERVICE_PAGE_SLUGS)[number]

/** `/<slug>` for every specialist page — the form the router and sitemap want. */
export const SERVICE_PAGE_PATHS: string[] = SERVICE_PAGE_SLUGS.map((s) => `/${s}`)

export function isServicePagePath(path: string): boolean {
  return SERVICE_PAGE_PATHS.includes(path)
}

export const servicePages: ServicePageInsert[] = [
  {
    slug: 'fractional-cto',
    label: 'Fractional CTO / Strategic Technology Advisory',
    audience: 'Founders, CEOs, boards, and executives without a full-time technology leader',
    h1: 'Fractional CTO advisory for payment, fintech, and digital-platform decisions.',
    intro:
      'Senior technology and payments judgment for organisations that need leadership capacity without a full-time executive hire. I work alongside founders, CEOs, boards, and delivery teams to improve architecture decisions, vendor choices, roadmaps, delivery governance, and operational control.',
    bestWhen: [
      'There is no senior technology owner in the room when the decision gets made.',
      'A complex payment or partner decision is on the table.',
      'A build needs oversight, or a stalled one needs recovery.',
      'A vendor or integration partner needs independent evaluation.',
      'The board needs a clear view of technology risk.',
    ],
    scope: [
      'Architecture and roadmap review',
      'Vendor and partner evaluation',
      'Delivery and risk oversight',
      'Board and executive technology reporting',
      'Engineering leadership support',
      'Payment and platform decision support',
    ],
    exclusions: [
      'Availability, governance cadence, and commercial terms are agreed privately rather than published here.',
      'This is an advisory seat, not a guarantee of delivery dates or outcomes.',
    ],
    engagementModel:
      'Three shapes: a one-off executive review, a short advisory sprint, or a monthly retainer. However it starts, the sequence is the same — diagnose, prioritise, advise and oversee, then transfer capability back to your team. Scope and availability by discussion.',
    ctaLabel: 'Discuss Fractional Advisory',
    ctaHref: '/contact?offer=fractional-cto-retainer',
    seoTitle: 'Fractional CTO Advisory for Fintech & Payment Platforms',
    seoDescription:
      'Senior technology and payments advisory for founders, executives, and boards navigating platform, delivery, vendor, and roadmap decisions.',
    claimStatus: 'proposed_positioning',
    published: false,
    sortOrder: 0,
  },
  {
    slug: 'technical-due-diligence',
    label: 'Technical Due Diligence & Technology Risk Review',
    audience: 'Investors, boards, acquirers, and institutional partners',
    h1: 'Independent technical due diligence for fintech and payment platforms.',
    intro:
      'A focused, independent review for investors, boards, acquirers, and institutional partners assessing a fintech, payment platform, or technology-enabled venture. I turn technical, product, payments, operational, and delivery evidence into a decision-ready risk view.',
    bestWhen: [
      'You are evaluating an investment, acquisition, or partnership and need the technology risk sized.',
      'An investment committee needs an independent read rather than the founder deck.',
      'Payment or settlement flows sit at the centre of the business and nobody in the room owns that expertise.',
      'Delivery has slipped and you need to know whether it is the team, the architecture, or the plan.',
    ],
    scope: [
      'Architecture and dependency assessment',
      'Payments and transaction-flow review',
      'Security and operational risk observations',
      'Team, vendor, and delivery assessment',
      'Prioritized findings',
      'Executive readout',
    ],
    exclusions: [
      'This is not legal, regulatory, accounting, or formal security certification work, unless separately qualified and contracted.',
      'Findings are observations drawn from the evidence made available, not an audit opinion.',
    ],
    engagementModel: 'Scoped per transaction. Depth, timeline, and readout format by discussion.',
    ctaLabel: 'Request a Diligence Discussion',
    ctaHref: '/contact?offer=technical-due-diligence',
    seoTitle: 'Technical Due Diligence for Fintech & Payment Platforms',
    seoDescription:
      'Decision-ready technology, product, payments, and operational risk review for investors, boards, and institutional partners.',
    claimStatus: 'proposed_positioning',
    published: false,
    sortOrder: 1,
  },
  {
    slug: 'payments-market-entry',
    label: 'Payments Market-Entry & Partner Readiness Advisory',
    audience: 'Payment and fintech businesses expanding into, or launching within, African markets',
    h1: 'Payments market entry, before the commitments get expensive.',
    intro:
      'Practical market-entry guidance for payment and fintech businesses expanding into, or launching within, African markets. I help clarify the customer proposition, payment rails, partner dependencies, operating model, launch sequence, and regulatory questions while they are still cheap to change.',
    bestWhen: [
      'A new market is on the roadmap and the rail and partner assumptions have not been tested.',
      'You are choosing between a direct integration, an aggregator, and a sponsor-bank arrangement.',
      'The commercial model works on the spreadsheet but nobody has costed the operating reality.',
      'A launch date exists and the dependency map does not.',
    ],
    scope: [
      'Market and rail assumptions log',
      'Partner and integration map',
      'Operating-model outline',
      'Launch risk register',
      'Phased entry roadmap',
    ],
    exclusions: [
      'This is not country-specific legal or regulatory advice. Where a market requires it, that work is scoped separately with local counsel or partners.',
      'Licensing outcomes and partner acceptance are decisions for the regulator and the counterparty, not deliverables of this engagement.',
    ],
    engagementModel: 'Scoped per market, usually phased. Duration and depth by discussion.',
    ctaLabel: 'Discuss Market Entry',
    ctaHref: '/contact?offer=payments-market-entry',
    seoTitle: 'Payments Market-Entry Advisory for African Markets',
    seoDescription:
      'Clarify payment rails, partner dependencies, operating model, and launch risks before a fintech or payment-platform expansion.',
    claimStatus: 'proposed_positioning',
    published: false,
    sortOrder: 2,
  },
  {
    slug: 'government-digital-collections',
    label: 'Government Digital Collections & Public-Sector Payments Advisory',
    audience: 'Public institutions, revenue authorities, pension funds, and government agencies',
    h1: 'Digital collections advisory for public institutions.',
    intro:
      'Advisory for public institutions improving how citizens and businesses pay for taxes, pensions, levies, fees, and services. The focus is payment-journey design, collection architecture, bank and PSP integration, reconciliation, controls, reporting, and adoption.',
    bestWhen: [
      'Collections still depend on counters, cash, or manual reconciliation.',
      'Multiple banks and PSPs are involved and the money is hard to trace end to end.',
      'A digital channel launched but adoption stalled.',
      'A provider needs to be selected and the evaluation criteria do not exist yet.',
    ],
    scope: [
      'Collection-journey map',
      'Payment-channel and integration options',
      'Settlement and reconciliation model',
      'Provider evaluation criteria',
      'Implementation roadmap',
      'Operating and governance considerations',
    ],
    exclusions: [
      'Nothing here represents government endorsement, a mandate, or the status of any current programme.',
      'Procurement decisions remain with the institution; this work produces evaluation criteria, not a supplier award.',
    ],
    engagementModel: 'Programme-scoped. Governance cadence and duration by discussion.',
    ctaLabel: 'Discuss a Collections Programme',
    ctaHref: '/contact?offer=government-digital-collections',
    seoTitle: 'Government Digital Collections & Public-Sector Payments Advisory',
    seoDescription:
      'Advisory on digital collections, payment journeys, reconciliation, controls, and operating models for public institutions.',
    claimStatus: 'proposed_positioning',
    published: false,
    sortOrder: 3,
  },
]
